import React from "react";
import type { Theme } from "../../types/Theme";
import {
    resolveChoiceIconSizeModern,
    resolveChoiceRadioIconSizeModern,
    resolveChoiceIconUncheckedColor,
    useModernChoiceStyles,
} from "../../theme/choiceThemeTokens";

interface ModernIconProps {
    theme: Theme;
    checked: boolean;
}

const CHECKBOX_CORNER_RADIUS = 3;
const CHECKBOX_UNCHECKED_STROKE_WIDTH = 2;

const RADIO_VIEW_BOX_SIZE = 18;
const RADIO_CENTER = RADIO_VIEW_BOX_SIZE / 2;
const RADIO_OUTER_RADIUS = 7;
const RADIO_RING_STROKE_WIDTH = 2;
const RADIO_INNER_RADIUS = 3;
const RADIO_UNCHECKED_STROKE_WIDTH = RADIO_RING_STROKE_WIDTH;

export function ModernCheckBoxIcon({ theme, checked }: ModernIconProps) {
    if (!useModernChoiceStyles(theme)) {
        return null;
    }

    const iconSize = resolveChoiceIconSizeModern(theme);

    if (checked) {
        return (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width={iconSize}
                height={iconSize}
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden
            >
                <rect
                    x="0.5"
                    y="0.5"
                    width="15"
                    height="15"
                    rx={CHECKBOX_CORNER_RADIUS}
                    fill={theme.PrimaryColor}
                />
                <path
                    d="M4 8L6.5 10.5L12 5.2"
                    stroke={theme.WhiteColor ?? "#FFFFFF"}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        );
    }

    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width={iconSize}
            height={iconSize}
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden
        >
            <rect
                x="1"
                y="1"
                width="14"
                height="14"
                rx={CHECKBOX_CORNER_RADIUS}
                stroke={resolveChoiceIconUncheckedColor(theme)}
                strokeWidth={CHECKBOX_UNCHECKED_STROKE_WIDTH}
                fill="none"
            />
        </svg>
    );
}

export function ModernRadioIcon({ theme, checked }: ModernIconProps) {
    if (!useModernChoiceStyles(theme)) {
        return null;
    }

    const iconSize = resolveChoiceRadioIconSizeModern(theme);
    const viewBox = `0 0 ${RADIO_VIEW_BOX_SIZE} ${RADIO_VIEW_BOX_SIZE}`;

    if (checked) {
        return (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width={iconSize}
                height={iconSize}
                viewBox={viewBox}
                fill="none"
                aria-hidden
            >
                <circle
                    cx={RADIO_CENTER}
                    cy={RADIO_CENTER}
                    r={RADIO_OUTER_RADIUS}
                    fill={theme.PrimaryColor}
                    stroke={theme.PrimaryColor}
                    strokeWidth={RADIO_RING_STROKE_WIDTH}
                />
                <circle
                    cx={RADIO_CENTER}
                    cy={RADIO_CENTER}
                    r={RADIO_INNER_RADIUS}
                    fill={theme.WhiteColor ?? "#FFFFFF"}
                />
            </svg>
        );
    }

    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width={iconSize}
            height={iconSize}
            viewBox={viewBox}
            fill="none"
            aria-hidden
        >
            <circle
                cx={RADIO_CENTER}
                cy={RADIO_CENTER}
                r={RADIO_OUTER_RADIUS}
                stroke={resolveChoiceIconUncheckedColor(theme)}
                strokeWidth={RADIO_UNCHECKED_STROKE_WIDTH}
                fill="none"
            />
        </svg>
    );
}
